import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GoToTop from '../components/GotToTop';
import { submitContactForm } from '../apis/authService';
import { ArrowLeft, Mail, Phone, Building2, Globe, FileText } from 'lucide-react';

const SponsorApplication = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    websiteUrl: '',
    message: '',
    countryCode: 'SE'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: ''
  });
  
  const countries = {
    SE: { name: 'Sweden', code: '+46', flag: 'https://flagcdn.com/w20/se.png', format: 'XX XXX XX XX', regex: /^[1-9]\d{8}$/, maxLength: 9, placeholder: '70 123 45 67' },
    ER: { name: 'Eritrea', code: '+291', flag: 'https://flagcdn.com/w20/er.png', format: 'X XXX XXX', regex: /^[1-9]\d{6}$/, maxLength: 7, placeholder: '1 234 567' },
    US: { name: 'United States', code: '+1', flag: 'https://flagcdn.com/w20/us.png', format: '(XXX) XXX-XXXX', regex: /^[2-9]\d{2}[2-9]\d{2}\d{4}$/, maxLength: 10, placeholder: '(555) 123-4567' },
    GB: { name: 'United Kingdom', code: '+44', flag: 'https://flagcdn.com/w20/gb.png', format: 'XXXX XXX XXXX', regex: /^[1-9]\d{9}$/, maxLength: 10, placeholder: '7700 900123' },
    DE: { name: 'Germany', code: '+49', flag: 'https://flagcdn.com/w20/de.png', format: 'XXX XXXXXXX', regex: /^[1-9]\d{9,10}$/, maxLength: 11, placeholder: '170 1234567' },
    FR: { name: 'France', code: '+33', flag: 'https://flagcdn.com/w20/fr.png', format: 'X XX XX XX XX', regex: /^[1-9]\d{8}$/, maxLength: 9, placeholder: '6 12 34 56 78' },
    IT: { name: 'Italy', code: '+39', flag: 'https://flagcdn.com/w20/it.png', format: 'XXX XXX XXXX', regex: /^[1-9]\d{9}$/, maxLength: 10, placeholder: '312 345 6789' }
  };

  const formatPhoneNumber = (value, countryCode) => {
    const country = countries[countryCode];
    if (!country) return value;
    
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    
    let formatted = '';
    let digitIndex = 0;
    
    for (let i = 0; i < country.format.length && digitIndex < digits.length; i++) {
      if (country.format[i] === 'X') {
        formatted += digits[digitIndex];
        digitIndex++;
      } else {
        formatted += country.format[i];
      }
    }
    
    return formatted;
  };

  const validatePhoneNumber = (phone, countryCode) => {
    const country = countries[countryCode];
    if (!country || !phone) return false;
    
    const digits = phone.replace(/\D/g, '');
    return country.regex.test(digits);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value, formData.countryCode);
      setFormData(prevState => ({
        ...prevState,
        [name]: formattedPhone
      }));
    } else if (name === 'countryCode') {
      setFormData(prevState => ({
        ...prevState,
        [name]: value,
        phone: ''
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      setSubmitStatus({
        success: false,
        message: 'Please fill in all required fields'
      });
      return;
    }

    if (!validatePhoneNumber(formData.phone, formData.countryCode)) {
      setSubmitStatus({
        success: false,
        message: `Please enter a valid phone number for ${countries[formData.countryCode].name}`
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });
    
    try {
      const submitData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: `${countries[formData.countryCode].code} ${formData.phone}`,
        message: `SPONSORSHIP APPLICATION\n\nCompany Name: ${formData.companyName || 'N/A'}\nWebsite: ${formData.websiteUrl || 'N/A'}\n\nMessage:\n${formData.message}`
      };
      
      const response = await submitContactForm(submitData);
      
      setSubmitStatus({
        success: true,
        message: response.message || 'Your sponsorship application has been submitted successfully! We will contact you soon.'
      });
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        websiteUrl: '',
        message: '',
        countryCode: 'SE'
      });
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: error.message || 'Failed to submit your application. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentCountry = countries[formData.countryCode];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GoToTop />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back
        </button>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Become a Sponsor</h1>
            <p className="text-gray-600">
              Join us in supporting our community events and festivals. Fill out the form below to apply for sponsorship.
            </p>
          </div>

          {submitStatus.message && (
            <div className={`mb-6 p-4 rounded-md ${
              submitStatus.success 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {submitStatus.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail size={20} className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(countries).map(([code, country]) => (
                    <option key={code} value={code}>
                      {country.code} {country.name}
                    </option>
                  ))}
                </select>
                <div className="relative flex-1">
                  <Phone size={20} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={currentCountry.placeholder}
                    maxLength={currentCountry.maxLength + 5}
                    required
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 size={16} className="inline mr-2" />
                Company Name
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe size={16} className="inline mr-2" />
                Website URL
              </label>
              <input
                type="url"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleChange}
                placeholder="https://example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText size={16} className="inline mr-2" />
                Message / Application Details <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                placeholder="Tell us about your company and why you'd like to become a sponsor..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SponsorApplication;

